import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl-next';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Modal, { ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';

import type { ClassificationOption } from '@atlassian/data-classification-level';

import type { ContentClassificationMetadata } from '../hooks/useContentClassification.ts';
import {
	useClassificationModalContext,
	useClassificationModalDispatchContext,
} from '../contexts/ClassificationModalContext.tsx';
import { useEditSubmitCallback } from '../hooks/useEditSubmitCallback.ts';
import {
	ActionSubjectId,
	useTrackClassificationAnalytics,
} from '../hooks/useTrackClassificationAnalytics.ts';

import { ClassificationEditViewPopup } from './ClassificationEditViewPopup.tsx';
import { sharedMessages } from './sharedMessages.ts';

type ClassificationEditModalProps = {
	contentId: string;
	spaceKey: string;
	contentClassificationMetadata: ContentClassificationMetadata;
};

export const ClassificationEditModal = ({
	contentId,
	spaceKey,
	contentClassificationMetadata,
}: ClassificationEditModalProps) => {
	const { isClassificationModalOpen } = useClassificationModalContext();
	const { setIsClassificationModalOpen } = useClassificationModalDispatchContext();
	const { trackEditPopupViewed } = useTrackClassificationAnalytics();

	const handleSubmit = useEditSubmitCallback({
		contentId,
		contentClassificationMetadata,
	});

	const { classification, spaceDefaultIsOverridden } = contentClassificationMetadata;
	const intl = useIntl();

	const defaultSelectedEditOption = spaceDefaultIsOverridden ? classification : undefined;

	const onEditSubmit = useCallback(
		(classificationOption: ClassificationOption) => {
			handleSubmit(classificationOption);
			setIsClassificationModalOpen(false);
		},
		[handleSubmit, setIsClassificationModalOpen],
	);

	const onEditCancel = useCallback(() => {
		setIsClassificationModalOpen(false);
	}, [setIsClassificationModalOpen]);

	useEffect(() => {
		if (isClassificationModalOpen) {
			trackEditPopupViewed(ActionSubjectId.GENERIC_DATA_CLASSIFICATION_MODAL_POPUP);
		}
	}, [isClassificationModalOpen, trackEditPopupViewed]);

	return (
		<ModalTransition>
			{isClassificationModalOpen && (
				<Modal onClose={() => setIsClassificationModalOpen(false)} width="330px">
					<ModalHeader hasCloseButton>
						<ModalTitle>{intl.formatMessage(sharedMessages.classifyContent)}</ModalTitle>
					</ModalHeader>
					<ClassificationEditViewPopup
						onEditSubmit={onEditSubmit}
						onEditCancel={onEditCancel}
						selectedOption={defaultSelectedEditOption ?? undefined}
						spaceKey={spaceKey}
						shouldShowHeading={false}
					/>
				</Modal>
			)}
		</ModalTransition>
	);
};
